const EVENT_PROPERTIES = {
  purchase_completed: ['plan', 'platform'],
  purchase_restored: ['plan', 'platform'],
  purchase_started: ['plan', 'platform'],
  push_opened: ['platform'],
  push_permission: ['granted', 'platform'],
  screen_view: ['screen', 'platform'],
};

const PROPERTY_VALUES = {
  granted: new Set([true, false]),
  plan: new Set(['free', 'pro', 'family']),
  platform: new Set(['web', 'ios', 'android']),
  screen: new Set([
    'billing',
    'dashboard',
    'login',
    'pricing',
    'settings',
    'signup',
  ]),
};

function configured(firebaseConfig) {
  return (
    firebaseConfig &&
    ['apiKey', 'appId', 'authDomain', 'measurementId', 'projectId'].every(
      (key) =>
        typeof firebaseConfig[key] === 'string' &&
        firebaseConfig[key].length > 0,
    )
  );
}

export function sanitizeAnalyticsEvent(name, properties = {}) {
  const allowedProperties = EVENT_PROPERTIES[name];
  if (!allowedProperties) return null;

  const sanitized = {};
  for (const property of allowedProperties) {
    const value = properties[property];
    if (PROPERTY_VALUES[property]?.has(value)) sanitized[property] = value;
  }

  return { name, properties: sanitized };
}

async function loadWebFirebase() {
  const [{ getApp, getApps, initializeApp }, analyticsModule] =
    await Promise.all([import('firebase/app'), import('firebase/analytics')]);
  return {
    async initialize(firebaseConfig) {
      if (!(await analyticsModule.isSupported())) return null;

      const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
      return analyticsModule.getAnalytics(app);
    },
    logEvent: analyticsModule.logEvent,
    setEnabled: analyticsModule.setAnalyticsCollectionEnabled,
  };
}

async function loadNativeFirebase() {
  const { FirebaseAnalytics } = await import('@capacitor-firebase/analytics');
  return FirebaseAnalytics;
}

export function createAnalytics({
  loadWeb = loadWebFirebase,
  loadNative = loadNativeFirebase,
} = {}) {
  let driver;
  let client;
  let nativeRuntime = false;
  let enabled = false;

  return {
    async initialize({ consent, native, config }) {
      if (!consent || !configured(config)) return false;

      nativeRuntime = native;
      driver = native ? await loadNative() : await loadWeb();
      if (native) {
        await driver.setEnabled({ enabled: true });
        client = driver;
      } else {
        client = await driver.initialize(config);
        if (!client) return false;
        driver.setEnabled(client, true);
      }
      enabled = true;
      return true;
    },

    async track(name, properties = {}) {
      if (!enabled) return false;

      const event = sanitizeAnalyticsEvent(name, properties);
      if (!event) return false;

      if (nativeRuntime) {
        await driver.logEvent({ name: event.name, params: event.properties });
      } else {
        driver.logEvent(client, event.name, event.properties);
      }
      return true;
    },

    async disable() {
      if (!driver) return false;

      if (nativeRuntime) {
        await driver.setEnabled({ enabled: false });
      } else if (client) {
        driver.setEnabled(client, false);
      }
      enabled = false;
      return true;
    },
  };
}

const analytics = createAnalytics();

export const initializeAnalytics = (options) => analytics.initialize(options);
export const trackEvent = (name, properties) =>
  analytics.track(name, properties);
export const disableAnalytics = () => analytics.disable();
